/**
 *
 * ParentPlanner
 *
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { find, uniq } from 'lodash-es';
import { Button, Card } from 'react-bootstrap';
import { setHours, startOfHour, endOfHour, isFuture, endOfDay } from 'utils/date-fns/index.js';
import Spinner from 'components/Spinner/index.jsx';
import { dateString } from 'utils/timeFormat.js';
import { useHash } from 'utils/urlUtils.js';
import { idValues } from 'utils/arrays.js';
import SmallCalendar from './SmallCalendar/index.jsx';
import messages from './messages.js';
import { MEETING_STATUS_ASSIGNED } from '../constants.js';
import mergeBlocks from './mergeBlocks.js';
import useStudentColor from './useStudentColor.js';
import getEvents from './getEvents.js';
import useParentMeetingSets from '../hooks/useParentMeetingSets.js';
import LargeCalendar from './LargeCalendar.jsx';

function ParentPlanner(props) {
  const { meetingId, students, student } = props;
  const sets = useParentMeetingSets();
  const { pathname } = useLocation();
  const hash = useHash();
  const currentSet = sets && mergeBlocks(sets[meetingId]); // memoized function already
  const dates = useMemo(() => currentSet && getDates(), [hash.block, currentSet]);
  const getColor = useStudentColor();

  function getDates() {
    const selectedBlockId = hash.block;
    if (selectedBlockId) {
      const selectedBlock = currentSet.blocks[selectedBlockId];
      return selectedBlock ? [dateString(selectedBlock.start)] : [];
    }
    return uniq(idValues(currentSet.blocks).map((o) => dateString(o.start)));
  }

  function handleClose() {
    hash.push({ block: null });
  }

  function getHours(events) {
    const minHours = Math.min(9, ...events.map((ev) => ev.start.getHours()));
    const maxHours = Math.max(22, ...events.map((ev) => ev.end.getHours()));
    const min = setHours(startOfHour(new Date()), minHours - 1);
    const max = setHours(endOfHour(new Date()), Math.min(23, maxHours + 1));
    const hours = Math.round((max.getTime() - min.getTime()) / 3600000);
    const currentBlockList = idValues(currentSet.blocks);
    const currentBlock = find(currentBlockList, (blk) => isFuture(endOfDay(blk.end))) || currentBlockList[0];
    const defaultDate = currentBlock?.end;
    return { min, max, hours, defaultDate };
  }

  function renderBody() {
    if (sets.loading) {
      return <Spinner />;
    }
    const { events, columnStudents } = getEvents({ currentSet, sets, student, students, meetingId, dates });
    const sharedProps = {
      events,
      students: columnStudents,
      ...getHours(events), // { min, max, hours, defaultDate }
    };
    const selectedBlock = events.find((event) => event.id === hash.block && event.student.id === student.userId);
    return (
      <Card.Body>
        {currentSet.status === MEETING_STATUS_ASSIGNED
          && <p className="intro-text"><FormattedMessage {...messages.changeHelp} /></p>}
        {selectedBlock ? (
          <SmallCalendar
            {...sharedProps}
            onClose={handleClose}
            getColor={getColor}
            student={student}
            block={selectedBlock}
            meetingSet={currentSet}
          />
        ) : (
          <LargeCalendar
            {...sharedProps}
            dates={dates}
          />
        )}
      </Card.Body>
    );
  }

  const title = currentSet ? currentSet.title : '…';
  const link = pathname.replace(/\/meetings.*/, '');
  return (
    <Card className="border-0 mb-0">
      <Card.Header>
        <h2 className="card-title"><FormattedMessage {...messages.header} values={{ title }} /></h2>
      </Card.Header>
      {currentSet && renderBody()}
      {!window.socsIsInAppWebView && (
        <Card.Footer className="border-top-0 text-end">
          <Button variant="light" href={link}><FormattedMessage {...messages.close} /></Button>
        </Card.Footer>
      )}
    </Card>
  );
}

ParentPlanner.propTypes = {
  meetingId: PropTypes.number,
  student: PropTypes.object,
  students: PropTypes.array, // eslint-disable-line react/no-unused-prop-types
};

export default ParentPlanner;
